import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { BellOff, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { SectionHeader } from '../../components/Shared';
import { ReminderSignup } from '../../components/election/ReminderSignup';

const RACE_LABEL = { mayor: 'Mayor', council: 'Ward Councillor', trustee: 'School Board Trustee', general: 'General' };

// /elections/reminders : sign up, or (with the tokenised link from an email)
// see current preferences and stop the emails.
export default function Reminders() {
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const token = params.get('token') || '';
  const [prefs, setPrefs] = useState(null);
  const [state, setState] = useState(email && token ? 'loading' : 'signup');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!email || !token) return;
    api.get('/election/reminders/preferences', { params: { email, token } })
      .then((r) => { setPrefs(r.data); setState('manage'); })
      .catch((err) => { toast.error(err?.response?.data?.detail || 'That link is not valid.'); setState('signup'); });
  }, [email, token]);

  const stop = async () => {
    setBusy(true);
    try {
      const r = await api.get('/election/reminders/unsubscribe', { params: { email, token } });
      toast.success(r.data?.message || 'Unsubscribed.');
      setState('stopped');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not unsubscribe.'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="election-reminders-page">
      <SectionHeader kicker="NEVER MISS A DEBATE" title="Weekly election event reminders" dek="One email every Monday morning with the week's debates, forums, meet and greets and coffee events across Kawartha Lakes. Pick a race or ward, or get everything." />
      {state === 'loading' && <p className="text-slate-600 inline-flex items-center gap-2" data-testid="reminders-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading your preferences</p>}
      {state === 'manage' && prefs && (
        <Card data-testid="reminders-manage"><CardContent className="p-5 sm:p-6">
          <p className="text-xs font-black uppercase tracking-widest text-[#24406f]">Your reminder settings</p>
          <p className="mt-1 font-bold text-[#1a2744]" data-testid="reminders-manage-email">{prefs.email}</p>
          <p className="text-sm text-slate-700 mt-1" data-testid="reminders-manage-filters">
            {prefs.active ? 'Active. ' : 'Stopped. '}
            {prefs.races?.length || prefs.wards?.length
              ? `Events for ${[...(prefs.races || []).map((r) => RACE_LABEL[r] || r), ...(prefs.wards || []).map((w) => `Ward ${w}`)].join(', ')} plus city-wide events.`
              : 'All election events in Kawartha Lakes.'}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {prefs.active && <Button onClick={stop} disabled={busy} variant="outline" className="h-11 border-red-300 text-red-800 font-bold btn-press bg-white" data-testid="reminders-stop-btn"><BellOff className="h-4 w-4 mr-1.5" /> Stop these emails</Button>}
            <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press bg-white" data-testid="reminders-events-link"><Link to="/elections/events">Election Event List</Link></Button>
          </div>
          <p className="text-xs text-slate-500 mt-4">To change which events you get, sign up again below with the same email address. Your settings will be replaced.</p>
        </CardContent></Card>
      )}
      {state === 'stopped' && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="reminders-stopped">
          <p className="font-bold text-[#1a2744] inline-flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> You will not receive any more election event emails.</p>
          <p className="text-sm text-slate-600 mt-1">Changed your mind? Sign up again below any time.</p>
        </div>
      )}
      <div className="mt-6"><ReminderSignup testId="reminders-page-signup" /></div>
      <p className="mt-6 text-xs text-slate-500 max-w-3xl">Your email address is used only for these reminders. It is never shown publicly, sold or shared. Every email carries a one-click unsubscribe link.</p>
    </div>
  );
}
